import { WorkOrder } from "../interfaces/work-order";
import { Api } from "@quipa/api";
import axiosInstance from "./axios-instance";
import { isMutationSuccess } from "./cache-utils";
import { invalidateOrderDataCache, resolveClientId, resolveOrderId } from "./cache-invalidation";

const getScopedAccountId = (accountId?: number): number => {
    if (typeof window === "undefined") {
        return Number(accountId || 0);
    }
    const ownerAccountId = Number(localStorage.getItem("ownerAccountId") || "0");
    if (ownerAccountId > 0) {
        return ownerAccountId;
    }
    if (Number(accountId) > 0) {
        return Number(accountId);
    }
    return Number(localStorage.getItem("accountId") || "0");
};

const withScopedAccountId = (payload: any) => {
    if (!payload || typeof payload !== "object") {
        return payload;
    }

    const scopedAccountId = getScopedAccountId(payload.accountId);
    if (!(scopedAccountId > 0)) {
        return payload;
    }

    return {
        ...payload,
        accountId: scopedAccountId,
    };
};

const getWorkOrderRecommendations = async (payload: WorkOrder) => {
    try {
        const panelJsonObject: Record<string, unknown> = {};
        const scopedPayload: any = withScopedAccountId(payload);

        if (
            scopedPayload &&
            scopedPayload.newDraperyDesign &&
            Array.isArray(scopedPayload.newDraperyDesign.panelJson)
        ) {
            scopedPayload.newDraperyDesign.panelJson.forEach((width: any, index: number) => {
                panelJsonObject[`pannel${index + 1}Width`] = width;
            });
            scopedPayload.newDraperyDesign.panelJson = panelJsonObject as typeof scopedPayload.newDraperyDesign.panelJson;
        }
        

        const response = await axiosInstance.post<WorkOrder>(
            "/workOrder/getWorkOrderRecommendations",
            scopedPayload
        );
        return response.data;
    } catch (error) {
        console.error("Error get work order recommendations:", error);
        throw error;
    }
};

const getWorkOrderRecommendationsRomanShade = async (payload: WorkOrder) => {
    try {
        const response = await axiosInstance.post<WorkOrder>(
            "/workOrder/getWorkOrderRecommendationsRomanShade",
            withScopedAccountId(payload)
        );
        return response.data;
    } catch (error) {
        console.error("Error get work order recommendations:", error);
        throw error;
    }
};

const getWorkOrderPDFContent = async (draperyOrderId: number) => {
    try {
        const response = await axiosInstance.get<any>(
            `/workOrder/getPDFContent?draperyOrderId=${draperyOrderId}`
        );
        return response.data;
    } catch (error) {
        console.error("Error get work order PDF content:", error);
        throw error;
    }
};
const addWorkOrder = async (payload: any) => {
    try {
        const scopedPayload = withScopedAccountId(payload);
        const response = await axiosInstance.post<any>(
            "/workOrder/add",
            scopedPayload
        );
        if (isMutationSuccess(response.data)) {
            invalidateOrderDataCache(resolveOrderId(scopedPayload, response.data), resolveClientId(scopedPayload, response.data));
        }
        return response.data;
    } catch (error) {
        console.error("Error add work order :", error);
        throw error;
    }
};
const editWorkOrder = async (payload: any) => {
    try {
        const scopedPayload = withScopedAccountId(payload);
        const response = await axiosInstance.put<any>(
            "/workOrder/edit",
            scopedPayload
        );
        if (isMutationSuccess(response.data)) {
            invalidateOrderDataCache(resolveOrderId(scopedPayload, response.data), resolveClientId(scopedPayload, response.data));
        }
        return response.data;
    } catch (error) {
        console.error("Error edit work order :", error);
        throw error;
    }
};
const getNextNum = async (accountId: number) => {
    try {
        const scopedAccountId = getScopedAccountId(accountId);
        const response = await axiosInstance.get<any>(
            `/workOrder/getNextNum?accountId=${scopedAccountId}`
        );
        return response.data;
    } catch (error) {
        console.error("Error get next num for work order :", error);
        throw error;
    }
};

const searchWorkOrders = async (accountId: number) => {
    try {
        const scopedAccountId = getScopedAccountId(accountId);
        const response = await axiosInstance.get<any>(
            `/workOrder/searchWorkOrders?accountId=${scopedAccountId}`
        );
        return response.data;
    } catch (error) {
        console.error("Error get next num for work order :", error);
        throw error;
    }
};
const saveWorkOrderOverrides = async (
    draperyOrderId: number,
    accountId: number,
    overrides: Pick<
        Api.SaveWorkOrderOverridesAction["Request"]["payload"],
        "panelJson" | "yardages"
    >,
    draperyOrderPriceId?: number
) => {
    try {
        const scopedAccountId = getScopedAccountId(accountId);
        const response = await axiosInstance.post<any>(
            `/workOrder/saveWorkOrderOverrides`,
            {
                draperyOrderId,
                accountId: scopedAccountId,
                panelJson: overrides.panelJson,
                yardages: overrides.yardages,
                draperyOrderPriceId,
            }
        );
        if (isMutationSuccess(response.data)) {
            invalidateOrderDataCache(draperyOrderId);
        }
        return response.data;
    } catch (error) {
        console.error(
            "Error save work order overrides for work order :",
            error
        );
        throw error;
    }
};

const handleDuplicateWorkOrders = async () => {
    try {
        const scopedAccountId = getScopedAccountId();
        const response = await axiosInstance.post<any>(
            `/workOrder/handleDuplicateWorkOrders?accountId=${scopedAccountId}`
        );
        if (isMutationSuccess(response.data)) {
            invalidateOrderDataCache();
        }
        return response.data;
    } catch (error) {
        console.error(
            "Error updating work order duplicates :",
            error
        );
        throw error;
    }
};

export {
    getWorkOrderRecommendations,
    getWorkOrderRecommendationsRomanShade,
    editWorkOrder,
    addWorkOrder,
    getNextNum,
    getWorkOrderPDFContent,
    saveWorkOrderOverrides,
    searchWorkOrders,
    handleDuplicateWorkOrders
};
